// Griglia di badge — mostra badge guadagnati e disponibili (locked)
import { Lock } from 'lucide-react'
import { Card } from '../../../components/ui/Card'
import { Skeleton } from '../../../components/ui/Skeleton'

export interface EarnedBadge {
  id: string
  name: string
  description: string | null
  icon: string | null
  points: number
  earnedAt: string
}

export interface AvailableBadge {
  id: string
  name: string
  description: string | null
  icon: string | null
  points: number
  conditionType: string | null
  conditionValue: number | null
}

interface BadgeDisplayProps {
  earnedBadges: EarnedBadge[]
  availableBadges: AvailableBadge[]
  isLoading?: boolean
}

function BadgeCard({
  name,
  description,
  icon,
  points,
  earned,
  earnedAt,
}: {
  name: string
  description: string | null
  icon: string | null
  points: number
  earned: boolean
  earnedAt?: string
}) {
  return (
    <div
      className={[
        'relative flex flex-col items-center gap-2 rounded-lg border p-4 text-center transition-all',
        earned
          ? 'border-[var(--fc-primary)] bg-[var(--fc-primary)]/5 shadow-sm'
          : 'border-[var(--fc-border)] bg-[var(--fc-surface)] opacity-50',
      ].join(' ')}
      title={description ?? name}
      aria-label={`Badge ${name}${earned ? ' (guadagnato)' : ' (non ancora guadagnato)'}`}
    >
      {/* Lock overlay per badge non guadagnati */}
      {!earned && (
        <span className="absolute right-2 top-2 text-[var(--fc-foreground-muted)]">
          <Lock className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
      )}

      {/* Icona / emoji */}
      <span className="text-3xl leading-none" aria-hidden="true">
        {icon ?? '🏅'}
      </span>

      {/* Nome badge */}
      <p className="text-xs font-semibold text-[var(--fc-foreground)] leading-tight">{name}</p>

      {/* Descrizione */}
      {description && (
        <p className="text-[10px] text-[var(--fc-foreground-muted)] leading-snug line-clamp-2">
          {description}
        </p>
      )}

      {/* Punti */}
      <span
        className={[
          'mt-auto rounded-full px-2 py-0.5 text-[10px] font-bold',
          earned
            ? 'bg-[var(--fc-primary)] text-white'
            : 'bg-[var(--fc-gray-200)] text-[var(--fc-foreground-muted)]',
        ].join(' ')}
      >
        +{points} pt
      </span>

      {/* Data acquisizione */}
      {earned && earnedAt && (
        <p className="text-[10px] text-[var(--fc-foreground-muted)]">
          {new Date(earnedAt).toLocaleDateString('it-IT')}
        </p>
      )}
    </div>
  )
}

export function BadgeDisplay({ earnedBadges, availableBadges, isLoading = false }: BadgeDisplayProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton key={i} height={140} className="rounded-lg" />
        ))}
      </div>
    )
  }

  const earnedIds = new Set(earnedBadges.map((b) => b.id))

  // Badge guadagnati prima, poi quelli disponibili (locked)
  return (
    <div className="space-y-6">
      {earnedBadges.length > 0 && (
        <section aria-labelledby="earned-badges-heading">
          <h3
            id="earned-badges-heading"
            className="mb-3 text-sm font-semibold text-[var(--fc-foreground)]"
          >
            Badge guadagnati ({earnedBadges.length})
          </h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {earnedBadges.map((badge) => (
              <BadgeCard
                key={badge.id}
                name={badge.name}
                description={badge.description}
                icon={badge.icon}
                points={badge.points}
                earned={true}
                earnedAt={badge.earnedAt}
              />
            ))}
          </div>
        </section>
      )}

      {availableBadges.length > 0 && (
        <section aria-labelledby="available-badges-heading">
          <h3
            id="available-badges-heading"
            className="mb-3 text-sm font-semibold text-[var(--fc-foreground-muted)]"
          >
            Da sbloccare ({availableBadges.filter((b) => !earnedIds.has(b.id)).length})
          </h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {availableBadges
              .filter((b) => !earnedIds.has(b.id))
              .map((badge) => (
                <BadgeCard
                  key={badge.id}
                  name={badge.name}
                  description={badge.description}
                  icon={badge.icon}
                  points={badge.points}
                  earned={false}
                />
              ))}
          </div>
        </section>
      )}

      {earnedBadges.length === 0 && availableBadges.length === 0 && (
        <Card>
          <p className="text-center text-sm text-[var(--fc-foreground-muted)]">
            Nessun badge disponibile. Completa interventi per sbloccarli.
          </p>
        </Card>
      )}
    </div>
  )
}
